import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultHRM1060_R01Dto, HRM1060_R01Dto } from './Dto_HRM1060_R01';

/** 화면 검색 조건이며 empNm은 화면 표시용이라 API 요청에서는 제외한다. */
export interface HRM1060_R01Filter {
    issueYear: string; // 발급 연도 YYYY
    certType: string; // 증명서 종류 코드
    issueSts: string; // 발급 상태 코드
    keyword: string; // 사번 또는 성명 검색어
    empNm: string; // 선택한 사원 표시명
}

/** 화면을 열 때 적용할 올해 연도와 빈 검색 조건이다. */
export const defaultHRM1060_R01Filter: HRM1060_R01Filter = {
    issueYear: String(new Date().getFullYear()), certType: '', issueSts: '', keyword: '', empNm: '',
};

/** 발급대장 행과 선택·삭제·검색·그리드 상태를 보관한다. */
export interface Store_HRM1060_R01 {
    HRM1060_R01Filter: HRM1060_R01Filter; // 현재 검색 조건
    HRM1060_R01PastFilter: HRM1060_R01Filter; // 직전 조회 조건
    setHRM1060_R01Filter: (filter: Partial<HRM1060_R01Filter>) => void; // 검색 조건 변경
    setHRM1060_R01PastFilter: (filter: Partial<HRM1060_R01Filter>) => void; // 직전 조건 기록
    HRM1060_R01List: HRM1060_R01Dto[]; // 현재 그리드 행
    setHRM1060_R01List: (list: HRM1060_R01Dto[]) => void; // 조회 목록 설정
    insertHRM1060_R01List: (data: HRM1060_R01Dto[], index?: number) => void; // 신규 행 추가
    removeHRM1060_R01List: (index: number) => void; // 신규 행 제거
    updateHRM1060_R01List: (index: number, data: HRM1060_R01Dto) => void; // 행 교체
    selectedHRM1060_R01List: string[]; // 선택 식별값
    addSelectedHRM1060_R01List: (ids: string[]) => void; // 단일 선택 교체
    removeSelectedHRM1060_R01List: (ids?: string[]) => void; // 선택 해제
    initSelectedHRM1060_R01List: () => void; // 선택 초기화
    allSelectedHRM1060_R01List: () => void; // 전체 선택
    deletedHRM1060_R01List: string[]; // 삭제 표시 식별값
    addDeletedHRM1060_R01List: (ids: string[]) => void; // 삭제 표시
    removeDeletedHRM1060_R01List: (ids?: string[]) => void; // 삭제 표시 해제
    originalHRM1060_R01List: HRM1060_R01Dto[]; // 서버 조회 원본
    setOriginalHRM1060_R01List: (list: HRM1060_R01Dto[]) => void; // 원본 보관
    columnState: ColumnState[] | null; // 저장된 컬럼 배치
    setColumnState: (state: ColumnState[] | null) => void; // 컬럼 배치 변경
    scrollTop: number | null; // 그리드 스크롤 위치
    setScrollTop: (top: number | null) => void; // 스크롤 위치 변경
    allCount: number; // 검색 조건의 서버 전체 건수
    setAllCount: (count: number) => void; // 전체 건수 변경
    initData: () => void; // 전체 화면 상태 초기화
}

/** API 호출 없이 HRM1060 화면 상태만 관리하는 Store이다. */
export const useHRM1060_R01Store = create<Store_HRM1060_R01>((set) => ({
    HRM1060_R01Filter: { ...defaultHRM1060_R01Filter },
    HRM1060_R01PastFilter: { ...defaultHRM1060_R01Filter },
    /** 현재 검색 조건의 전달된 필드만 갱신한다. */
    setHRM1060_R01Filter: filter => set(state => ({ HRM1060_R01Filter: { ...state.HRM1060_R01Filter, ...filter } })),
    /** 직전 검색 조건의 전달된 필드만 갱신한다. */
    setHRM1060_R01PastFilter: filter => set(state => ({ HRM1060_R01PastFilter: { ...state.HRM1060_R01PastFilter, ...filter } })),
    HRM1060_R01List: [],
    /** 서버 행에 화면 기본값과 안정적인 그리드 인덱스를 더한다. */
    setHRM1060_R01List: list => set({ HRM1060_R01List: list.map((item, index) => ({
        ...defaultHRM1060_R01Dto, ...item,
        valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
        orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
        orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
    })) }),
    /** 기준 행 아래 또는 목록 끝에 신규 행을 넣는다. */
    insertHRM1060_R01List: (data, index) => set(state => {
        const list = [...state.HRM1060_R01List]; // 삽입할 목록 사본
        const target = list.find(item => item.valueIndex === index); // 기준 행
        const orderIndex = target?.orderIndex ?? 0; // 기준 정렬값
        const orderIndexSeq = (target?.orderIndexSeq ?? 0) + 1; // 같은 기준 행 아래의 정렬 순서
        const maxIndex = Math.max(0, ...list.map(item => item.valueIndex)); // 새 식별값 시작점
        const rows = data.map((item, offset) => ({
            ...defaultHRM1060_R01Dto, ...item,
            valueIndex: maxIndex + offset + 1, orderIndex, orderIndexSeq,
        })); // 새 행에 고유 식별값 부여
        if (index !== undefined) list.splice(Math.max(0, list.findIndex(item => item.valueIndex === index) + 1), 0, ...rows);
        else list.push(...rows);
        return { HRM1060_R01List: list };
    }),
    /** 신규 행을 제거하고 해당 선택·삭제 표시도 지운다. */
    removeHRM1060_R01List: index => set(state => ({
        HRM1060_R01List: state.HRM1060_R01List.filter(item => item.valueIndex !== index),
        selectedHRM1060_R01List: state.selectedHRM1060_R01List.filter(id => id !== String(index)),
        deletedHRM1060_R01List: state.deletedHRM1060_R01List.filter(id => id !== String(index)),
    })),
    /** valueIndex가 같은 행만 최신 데이터로 교체한다. */
    updateHRM1060_R01List: (index, data) => set(state => ({
        HRM1060_R01List: state.HRM1060_R01List.map(item => item.valueIndex === index ? data : item),
    })),
    selectedHRM1060_R01List: [],
    /** 행 클릭 선택은 누적하지 않고 전달된 한 행으로 교체한다. */
    addSelectedHRM1060_R01List: ids => set({ selectedHRM1060_R01List: ids.map(String) }),
    /** 지정한 식별값 또는 전체 선택을 해제한다. */
    removeSelectedHRM1060_R01List: ids => set(state => ({
        selectedHRM1060_R01List: ids
            ? state.selectedHRM1060_R01List.filter(id => !ids.map(String).includes(id)) : [],
    })),
    /** 선택을 모두 지운다. */
    initSelectedHRM1060_R01List: () => set({ selectedHRM1060_R01List: [] }),
    /** 현재 모든 데이터 행을 선택한다. */
    allSelectedHRM1060_R01List: () => set(state => ({
        selectedHRM1060_R01List: state.HRM1060_R01List.map(item => String(item.valueIndex)),
    })),
    deletedHRM1060_R01List: [],
    /** 삭제 표시를 중복 없이 저장한다. */
    addDeletedHRM1060_R01List: ids => set(state => ({
        deletedHRM1060_R01List: Array.from(new Set([...state.deletedHRM1060_R01List, ...ids.map(String)])),
    })),
    /** 지정한 표시 또는 모든 표시를 지운다. */
    removeDeletedHRM1060_R01List: ids => set(state => ({
        deletedHRM1060_R01List: ids
            ? state.deletedHRM1060_R01List.filter(id => !ids.map(String).includes(id)) : [],
    })),
    originalHRM1060_R01List: [],
    /** 서버 원본에 그리드 인덱스를 부여해 변경 비교에 사용한다. */
    setOriginalHRM1060_R01List: list => set({ originalHRM1060_R01List: list.map((item, index) => ({
        ...defaultHRM1060_R01Dto, ...item, valueIndex: index + 1, orderIndex: index + 1, orderIndexSeq: 0,
    })) }),
    columnState: null,
    /** 컬럼 배치를 갱신한다. */
    setColumnState: columnState => set({ columnState }),
    scrollTop: null,
    /** 스크롤 위치를 갱신한다. */
    setScrollTop: scrollTop => set({ scrollTop }),
    allCount: 0,
    /** 서버 건수를 갱신한다. */
    setAllCount: allCount => set({ allCount }),
    /** 모든 화면 전용 상태를 안전한 기본값으로 돌린다. */
    initData: () => set({
        HRM1060_R01Filter: { ...defaultHRM1060_R01Filter },
        HRM1060_R01PastFilter: { ...defaultHRM1060_R01Filter },
        HRM1060_R01List: [], selectedHRM1060_R01List: [], deletedHRM1060_R01List: [],
        originalHRM1060_R01List: [], columnState: null, scrollTop: null, allCount: 0,
    }),
}));
